import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Moment from "react-moment";
import ContentContainer from "../../../templates/content-container/contentContainer";
import Loading from "../../../ui-components/loading/loading";
import {
  confirmPaymentReturn,
  paymentSelector,
  resetPayment,
  startPaymentFlow,
} from "../../../slices/payment/paymentSlice";
import { PaymentStatusResponse } from "../../../app/data/payment/PaymentSession";
import Amount from "../../../ui-components/molecules/amount/amount";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import ImportantMessage, { ImportantMessageTheme } from "ui-components/molecules/important-message/importantMessage";
import { userSelector } from "../../../slices/user/userSlice";
import { UserUtils } from "../../../app/data/user/userUtils";
import { Routes } from "../../../app/route/RoutesConfig";
import XGSIcons from "../../../ui-components/icon/xgsIcons";
import XGSIcon from "../../../ui-components/icon/xgsIcon";
import LabeledValue, {
  LabeledValueDirection,
  LabeledValueValueSize,
} from "../../../ui-components/molecules/labeled-value/labeledValue";
import PdfActionSplitButton from "../../../ui-components/pdf-action-split-button/pdfActionSplitButton";
import {
  deliverPdf,
  getPdfDownloadErrorMessage,
} from "../../../app/data/shipping-docs/downloadPdf";
import { PdfDocumentAction } from "../../../app/data/shipping-docs/pdfDocumentActionStorage";
import PaymentService from "../../../app/data/payment/paymentService";
import { InvoicesPaid, PaymentSummary } from "../../payments/paymentAmountBreakdown";
import "./paymentReturn.scss";

const TERMINAL = new Set(["COMPLETED", "FAILED", "CANCELLED", "EXPIRED"]);
const MAX_POLLS = 20;
const paymentService = PaymentService.getInstance();

const paymentMethodLabel = (method?: string): string => {
  if (method === "CC") return "Credit Card";
  if (method === "CK" || method === "BANK") return "eCheck";
  return method || "—";
};

const PaymentReturn: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const payment = useSelector(paymentSelector);
  const userState = useSelector(userSelector);
  const location = useLocation();
  const [status, setStatus] = useState<PaymentStatusResponse | null>(null);
  const [message, setMessage] = useState("Confirming your payment…");
  const [pollExhausted, setPollExhausted] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const polls = useRef(0);
  const runIdRef = useRef(0);

  const paymentId = new URLSearchParams(location.search).get("paymentId");
  const providerReturnStatus = new URLSearchParams(location.search).get("status");
  const returnToParam = new URLSearchParams(location.search).get("returnTo");
  const safeReturnTo =
    returnToParam &&
    returnToParam.startsWith("/") &&
    !returnToParam.startsWith("//") &&
    !returnToParam.includes("://")
      ? returnToParam
      : null;

  const runConfirm = useCallback(
    async (runId: number) => {
      if (!paymentId) {
        setMessage("Missing payment id.");
        return;
      }
      const result: any = await dispatch(
        confirmPaymentReturn(paymentId, providerReturnStatus) as any
      );
      if (runId !== runIdRef.current) return;
      const data = result as PaymentStatusResponse | null;
      if (!data) {
        setMessage("Unable to confirm payment status.");
        return;
      }
      setStatus(data);
      if (TERMINAL.has(data.status)) {
        setPollExhausted(false);
        return;
      }
      setMessage("Still confirming with the payment provider…");
      if (polls.current < MAX_POLLS) {
        polls.current += 1;
        setTimeout(() => {
          if (runId === runIdRef.current) {
            runConfirm(runId);
          }
        }, 3000);
      } else {
        setPollExhausted(true);
      }
    },
    [dispatch, paymentId, providerReturnStatus]
  );

  const startConfirmLoop = useCallback(() => {
    runIdRef.current += 1;
    const runId = runIdRef.current;
    polls.current = 0;
    setPollExhausted(false);
    setMessage("Confirming your payment…");
    runConfirm(runId);
  }, [runConfirm]);

  useEffect(() => {
    setStatus(null);
    if (!paymentId) {
      setMessage("Missing payment id.");
      return;
    }
    startConfirmLoop();
    return () => {
      runIdRef.current += 1;
    };
  }, [paymentId, startConfirmLoop]);

  useEffect(() => {
    if (payment.confirmStatus) {
      setStatus(payment.confirmStatus);
    }
  }, [payment.confirmStatus]);

  const accountPrefix = userState.activeSubAccount?.accountNumber
    ? `/${userState.activeSubAccount.accountNumber}`
    : status?.accountNumber
    ? `/${status.accountNumber}`
    : "";

  const terminalPrefix = userState.activeTerminal?.id
    ? `/${userState.activeTerminal.id}`
    : "";

  const navigateBack = () => {
    dispatch(resetPayment());
    if (safeReturnTo) {
      history.push(safeReturnTo);
      return;
    }
    const invoices = status?.invoiceNumbers || [];
    if (UserUtils.isCustomer(userState.profile) && invoices.length === 1 && accountPrefix) {
      history.push(`${accountPrefix}${Routes.invoices}/${invoices[0]}`);
      return;
    }
    if (UserUtils.isCustomer(userState.profile) && accountPrefix) {
      history.push(`${accountPrefix}${Routes.agingInvoices}/_TOTAL`);
      return;
    }
    if (UserUtils.isEmployee(userState.profile) && invoices.length === 1 && terminalPrefix) {
      history.push(`${terminalPrefix}${Routes.shipments.tracking}/${invoices[0]}`);
      return;
    }
    if (UserUtils.isEmployee(userState.profile) && terminalPrefix) {
      history.push(`${terminalPrefix}${Routes.home}`);
      return;
    }
    if (window.history.length > 1) {
      history.goBack();
      return;
    }
    history.push(Routes.home);
  };

  const refreshStatus = () => {
    startConfirmLoop();
  };

  const onPdfAction = async (action: PdfDocumentAction) => {
    if (!status?.paymentId) return;
    setPdfBusy(true);
    setPdfError(null);
    try {
      const res = await paymentService.getPaymentReceiptPdf(status.paymentId);
      if (!res.ok() || !res.data) {
        throw new Error(
          typeof res.getError?.() === "string"
            ? (res.getError() as string)
            : "Failed to download receipt PDF"
        );
      }
      deliverPdf(res.data, `payment-receipt-${status.paymentId}.pdf`, action);
    } catch (e) {
      setPdfError(getPdfDownloadErrorMessage(e, "Failed to download receipt PDF"));
    } finally {
      setPdfBusy(false);
    }
  };

  const payAgain = () => {
    const invoices = status?.invoiceNumbers || [];
    if (!invoices.length) {
      navigateBack();
      return;
    }
    dispatch(resetPayment());
    dispatch(
      startPaymentFlow({
        invoiceNumbers: invoices,
        paymentSum: status?.amountCents != null ? status.amountCents / 100 : undefined,
        initiator: invoices.length > 1 ? "list" : "details",
      })
    );
  };

  const dollars =
    status?.amountCents != null ? status.amountCents / 100 : undefined;
  const isPendingOpen =
    !!status && !TERMINAL.has(status.status) && pollExhausted;
  const isConfirming =
    !!status && !TERMINAL.has(status.status) && !pollExhausted;

  return (
    <ContentContainer title="Payment status">
      <div className="payment-return">
        <Loading isLoading={payment.loading && !status} />
        {payment.error && <XGSErrorMessage>{payment.error}</XGSErrorMessage>}
        {!status && <p>{message}</p>}

        {status && (
          <div className="payment-return__body">
            {status.status === "COMPLETED" && (
              <div className="payment-return__receipt payment-receipt success">
                <XGSIcon
                  className="payment-receipt__icon"
                  icon={XGSIcons.faCheckCircle}
                />
                <div className="payment-receipt__message">Payment successful</div>
                <div className="payment-receipt__transaction">
                  Reference: {status.paymentId}
                </div>
                {dollars != null && (
                  <PaymentSummary
                    amountCents={status.amountCents}
                    surchargeAmountCents={status.surchargeAmountCents}
                    invoices={status.invoices}
                  />
                )}
                <div className="payment-receipt__details">
                  {(status.dateUpdated || status.dateCreated) && (
                    <LabeledValue
                      label="DATE PAID"
                      value={
                        <Moment utc local format="MM/DD/YYYY h:mm A">
                          {status.dateUpdated || status.dateCreated}
                        </Moment>
                      }
                      direction={LabeledValueDirection.vertical}
                      valueSize={LabeledValueValueSize.medium}
                      extraClassName="payment-receipt__details__item"
                    />
                  )}
                  {status.paidBy && (
                    <LabeledValue
                      label="PAID BY"
                      value={status.paidBy}
                      direction={LabeledValueDirection.vertical}
                      valueSize={LabeledValueValueSize.medium}
                      extraClassName="payment-receipt__details__item"
                    />
                  )}
                  <LabeledValue
                    label="PAYMENT METHOD"
                    value={paymentMethodLabel(status.paymentMethod)}
                    direction={LabeledValueDirection.vertical}
                    valueSize={LabeledValueValueSize.medium}
                    extraClassName="payment-receipt__details__item"
                  />
                </div>
                <InvoicesPaid invoices={status.invoices} />
                <div className="payment-receipt__bottom">
                  If you have any questions, contact us at <b>info@xgsi.com</b> or
                  call <b>(844) 947-7447</b>
                </div>
              </div>
            )}

            {status.status === "FAILED" && (
              <div className="payment-return__outcome">
                <h3>Payment failed</h3>
                <p>This payment was not completed. You can try paying again.</p>
              </div>
            )}

            {(status.status === "CANCELLED" || status.status === "EXPIRED") && (
              <div className="payment-return__outcome">
                <h3>Payment was not completed</h3>
                <p>
                  {status.status === "EXPIRED"
                    ? "This payment session expired before it was finished."
                    : "This payment was cancelled."}{" "}
                  You can start a new payment when ready.
                </p>
              </div>
            )}

            {isConfirming && (
              <div className="payment-return__outcome">
                <h3>{message}</h3>
                {dollars != null && (
                  <p>
                    Amount: <Amount amount={dollars} />
                  </p>
                )}
                {status.invoiceNumbers?.length ? (
                  <p>Invoice(s): {status.invoiceNumbers.join(", ")}</p>
                ) : null}
                <p>Reference: {status.paymentId}</p>
              </div>
            )}

            {isPendingOpen && (
              <div className="payment-return__outcome">
                <ImportantMessage theme={ImportantMessageTheme.BLUE}>
                  Your payment is still processing with the provider. The invoice
                  will update automatically when confirmation arrives. You can
                  refresh status now, or check again later from the invoice.
                </ImportantMessage>
                {dollars != null && (
                  <p>
                    Amount: <Amount amount={dollars} />
                  </p>
                )}
                {status.invoiceNumbers?.length ? (
                  <p>Invoice(s): {status.invoiceNumbers.join(", ")}</p>
                ) : null}
                <p>Reference: {status.paymentId}</p>
              </div>
            )}

            <div className="payment-return__actions no-print">
              {pdfError && <XGSErrorMessage>{pdfError}</XGSErrorMessage>}
              {status.status === "COMPLETED" && (
                <PdfActionSplitButton onAction={onPdfAction} spinner={pdfBusy}>
                  Receipt
                </PdfActionSplitButton>
              )}
              {isPendingOpen && (
                <Button theme={ButtonThemes.blue} onClick={refreshStatus}>
                  Refresh status
                </Button>
              )}
              {(status.status === "FAILED" ||
                status.status === "CANCELLED" ||
                status.status === "EXPIRED") &&
                !!status.invoiceNumbers?.length && (
                  <Button theme={ButtonThemes.blue} onClick={payAgain}>
                    Pay again
                  </Button>
                )}
              <Button theme={ButtonThemes.gray} onClick={navigateBack}>
                {status.status === "COMPLETED" ? "Done" : "Back"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </ContentContainer>
  );
};

export default PaymentReturn;
